import { HttpClient, HttpErrorResponse, HttpEventType, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { OperationCanceledException } from '../exceptions/operation-canceled-exception';
import { FileBlobResponse } from '../models/files/file-blob-response';
import { ApiHttpResponseError } from '../models/http/api-http-response-error';
import { FileResponse } from '../models/files/file-response';
import { ApiHttpClientNotificationService } from './api-http-client-notification.service';
import { ApiFileDownloadProgressHandler } from './api-file-download-progress-handler';

@Injectable()
export class ApiHttpClient {
      protected baseUrl: string = 'api/';

  constructor(private readonly http: HttpClient, private $injector: Injector) {
    
  }

  getFileAsync(url: string, data?: any, timeout?: Promise<any>, 
                  showNotificationOnError = true, progressHandler?: ApiFileDownloadProgressHandler): Promise<any> {
        const promise = new Promise<FileResponse>((resolve, reject) => {
          let httpParams = new HttpParams();

      if (data) {
            try {
              Object.keys(data).forEach(function (key) {
                const value = data[key];

            if (value instanceof (Array)) {
                  (value as Array<any>).forEach((item) => {
                    httpParams = httpParams.append(key, item);
              })
            } else if (value != undefined && value !== '') {
                  httpParams = httpParams.append(key, data[key]);
            }
          });
        }
        catch (error) {
              reject(error);
          return;
        }
      }

      const httpRequest = new HttpRequest<any>("GET", this.baseUrl + url, { responseType: 'blob', params: httpParams, reportProgress: progressHandler != undefined });

      const requestSubscription = this.http.request(httpRequest).subscribe(event => {
                    if (event.type === HttpEventType.DownloadProgress) {
                      if(progressHandler){
                        progressHandler(event.loaded, event.total);
                  }
                }
                else if (event instanceof HttpResponse) {
                      const response = event as HttpResponse<Blob>;
                  const error = response.headers.get("x-file-download-error");
          
                  if (error) {
                        this.processOnError({ message: error }, reject, showNotificationOnError);
                    return;
                  }
          
                  const contentType: string = response.headers.get('Content-Type');
                  const contentDispositionHeader: string = response.headers.get('Content-Disposition');
                  const parts: string[] = contentDispositionHeader.split(';');
                  const fileName = parts[1].split('=')[1];

                  const fileBlobResponse = new FileBlobResponse(fileName, contentType, response.body, this.$injector);

                  resolve(fileBlobResponse);
                }
              },(e) => {
                    this.processOnError(e, reject, showNotificationOnError);
              });

      // if (timeout) {
          //   timeout.then(() => {
          //     requestSubscription.unsubscribe();
      //     reject(new OperationCanceledException());
      //   });
      // }
    });

    return promise;
  }

  getJsonAsync<T>(url: string, data?: any, timeout?: Promise<any>, showNotificationOnError = true): Promise<T> {
        const promise = new Promise<T>((resolve, reject) => {
    
      let httpParams = new HttpParams();

      if (data) {
            try {
              Object.keys(data).forEach(function (key) {
                const value = data[key];
            if (value instanceof (Array)) {
                  (value as Array<any>).forEach((item) => {
                    httpParams = httpParams.append(key, item);
              })
            } else if (value != undefined && value !== '') {
                  httpParams = httpParams.append(key, data[key]);
            }
          });
        }
        catch (error) {
              reject(error);
          return;
        }
      }
      
      let request = this.http.get<ApiResponse<T>>(this.baseUrl + url, { params: httpParams })
        .subscribe(response => {
              if (!response) {
                this.processOnError({ message: "No response" }, reject, showNotificationOnError);
          } else if (response.isSuccess !== true) {
                this.processOnError(response.error, reject, showNotificationOnError);
          } else {
                resolve(response.data);
          }
        }, error => {
              if (error instanceof HttpErrorResponse) {
                this.processOnError({ message: error.statusText }, reject, showNotificationOnError);
          }
          else if (error instanceof Error) {
            this.processOnError({ message: 'Error.' }, reject, showNotificationOnError);
          }
          else {
                this.processOnError({ message: JSON.stringify(error) }, reject, showNotificationOnError);
          }
        });

      if (timeout) {
            timeout.then(() => {
              request.unsubscribe();
          reject(new OperationCanceledException());
        });
      }
    });

    return promise;
  }

  postJsonAsync<T>(url: string, body?: any, timeout?: Promise<any>, showNotificationOnError = true): Promise<T> {
        let promise = new Promise<T>((resolve, reject) => {
    
      let request = this.http.post<ApiResponse<T>>(this.baseUrl + url, body)
        .subscribe(response => {
              if (!response) {
                this.processOnError({ message: "No response" }, reject, showNotificationOnError);
          } else if (response.isSuccess !== true) {
                this.processOnError(response.error, reject, showNotificationOnError);
          } else {
                resolve(response.data);
          }
        }, error => {
              if (error instanceof HttpErrorResponse) {
                this.processOnError({ message: error.statusText }, reject, showNotificationOnError);
          }
          else if (error instanceof Error) {
            this.processOnError({ message: 'Error.' }, reject, showNotificationOnError);
          }
          else {
                this.processOnError({ message: JSON.stringify(error) }, reject, showNotificationOnError);
          }
        });

      if (timeout) {
            timeout.then(() => {
              request.unsubscribe();
          reject(new OperationCanceledException());
        });
      }
    });

    return promise;
  }

  postFileAsync<T>(url: string, file: File, body?: any, timeout?: Promise<any>, showNotificationOnError = true): Promise<T> {
        let promise = new Promise<T>((resolve, reject) => {
          let formData: FormData = new FormData();

      formData.append('upload', file, file.name);
      formData.append('request', JSON.stringify(body));

      const uploadReq = new HttpRequest('POST', this.baseUrl + url, formData, {
            reportProgress: true,
        params: body
      });

      var request = this.http.request<ApiResponse<T>>(uploadReq)
        .subscribe((event: any) => { // .subscribe((event: HttpResponse<ApiResponse<T>>) => { previous code ANGULAR CHANGES
              if (event.type != HttpEventType.Response) return;

          let response = event.body;

          if (!response) {
                this.processOnError({ message: "No response" }, reject, showNotificationOnError);
          } else if (response.isSuccess !== true) {
                this.processOnError(response.error, reject, showNotificationOnError);
          } else {
                resolve(response.data);
          }
        }, error => {
              if (error instanceof HttpErrorResponse) {
                this.processOnError({ message: error.statusText }, reject, showNotificationOnError);
          }
          else if (error instanceof Error) {
            this.processOnError({ message: 'Error.' }, reject, showNotificationOnError);
          }
          else {
                this.processOnError({ message: JSON.stringify(error) }, reject, showNotificationOnError);
          }
        });

      if (timeout) {
            timeout.then(() => {
              request.unsubscribe();
          reject(new OperationCanceledException());
        });
      }
    });

    return promise;
  }

  private processOnError(error: ApiHttpResponseError, rejectExecutor: (reason?: any) => void, showNotificationOnError: boolean) {
    
    rejectExecutor(error.message);

    if (showNotificationOnError) {
    
      try {
            const notificationService = this.$injector.get(ApiHttpClientNotificationService) as ApiHttpClientNotificationService;

        if (notificationService) {
              notificationService.notifyError(error);
        }
      }
      catch (e) {
            console.error(`ApiHttpClient Error: ${e}`);
      }
    }
  }
}

interface ApiResponse<T = any> {
      isSuccess: boolean;

  data: T;

  processingTime: string;

  error: { message: string };
}
